'use client';
import { useEffect, useRef } from 'react';
import { openobserveRum } from '@openobserve/browser-rum';
import { openobserveLogs } from '@openobserve/browser-logs';
import { useLicSettings } from '@/domains/config/hooks/use-lic-settings';
import { OneDomainService } from '@/services/onedomain.service';
import { useAuthData, useAuthToken } from '@/store/auth';
import { useAppearanceStore } from '@/store/appearance';
import { useAccountInfo } from '@/hooks/query/account';
import { useOpenObserveViewTracking } from '@/hooks/use-openobserve-view-tracking';
import { getCDNInfo } from '../cdn-info-provider';
import { markOpenObserveReady } from './openobserve-ready-store';

const OPENOBSERVE_ORGANIZATION_IDENTIFIER = 'default';
const getOpenObserveApplicationId = (environment: string) => `galaxy-web-rum-${environment.toLowerCase()}`;

export const OpenObserveProvider = () => {
	const { data: licSettingsData } = useLicSettings();
	const view = useAppearanceStore(state => state.view);
	const authToken = useAuthToken();
	const authData = useAuthData();
	const { user } = useAccountInfo();
	const isBefore = !authToken; // Only track logged-in users

	// Ensure init is called only once
	const isInitializedRef = useRef(false);
	const latestUserContextRef = useRef<{ id?: string; siteName?: string; userName?: string }>({});
	useEffect(() => {
		latestUserContextRef.current = {
			id: user?.ID ? String(user.ID) : undefined,
			siteName: authData?.siteName || undefined,
			userName: authData?.userName || undefined,
		};
	}, [user?.ID, authData?.siteName, authData?.userName]);

	// Track route changes for OpenObserve RUM views
	useOpenObserveViewTracking();

	// Effect 1: Initialize OpenObserve ONCE
	useEffect(() => {
		// Only track logged-in users
		if (isBefore) {
			return;
		}

		// Wait for LicSettings to load from api-backend
		if (!licSettingsData) {
			return;
		}

		if (!user?.ID || !authData?.siteName || !authData?.userName) {
			return;
		}

		// Validate required config values are non-empty strings
		const clientToken = licSettingsData.sportsbook?.openObserveClientToken ?? '';
		const site = licSettingsData.sportsbook?.openObserveSite ?? '';
		const isValidConfig =
			Boolean(licSettingsData.sportsbook?.enableOpenObserve) &&
			clientToken.length > 0 &&
			site.length > 0;

		if (!isValidConfig) {
			console.warn('⚠️ OpenObserve: Config validation failed', {
				enabled: licSettingsData.sportsbook?.enableOpenObserve,
				tokenValue: clientToken.substring(0, 8) + '...',
				site
			});
			return;
		}

		if (isInitializedRef.current) {
			return;
		}

		if (isValidConfig && !isInitializedRef.current) {
			try {
			// Layer 1: Protect entire OpenObserve initialization
				const environment = window?.location?.hostname?.includes('localhost') ? 'LOCAL' : OneDomainService.getEnvironmentFromOneDomain();
				const service = `galaxy-mars-${environment.toLowerCase()}`;

				openobserveRum.setUser({
					id: String(user.ID),
					// Standard User field the O2 Sessions/Replay UI keys the user display on
					// (without it the list shows "Unknown"; it doesn't read our custom userName)
					email: authData?.userName || undefined,
					siteName: authData?.siteName || undefined,
					userName: authData?.userName || undefined,
				});

				openobserveRum.init({
					applicationId: getOpenObserveApplicationId(environment),
					clientToken,
					site,
					organizationIdentifier: OPENOBSERVE_ORGANIZATION_IDENTIFIER,
					apiVersion: 'v1',
					insecureHTTP: false,
					service,
					env: environment.toLowerCase(),
					version: process.env.NEXT_PUBLIC_FILE_VERSION_ID,
					sessionSampleRate: licSettingsData.sportsbook?.openObserveSampleRateList?.[0] ?? 0,
					sessionReplaySampleRate: licSettingsData.sportsbook?.openObserveSampleRateList?.[1] ?? 0,
					defaultPrivacyLevel: 'mask-user-input',
					trackUserInteractions: true,
					trackResources: true,
					trackLongTasks: true,
					trackViewsManually: true, // 手動追蹤視圖，防止自動產生 ???? 的視圖名稱
					beforeSend: (event) => {
						try {
							// Drop the O2 SDK's own failed-upload errors. This fork's intake
							// exclusion (isIntakeUrl) still checks Datadog's dd* params instead of
							// o2*, so the SDK tracks its own upload requests and reports failures as
							// RUM errors like "Fetch error POST https://.../rum?o2source=browser&o2-api-key=...".
							// These are infra noise, not app errors, so drop them.
							if (event.type === 'error') {
								const message = event.error?.message ?? '';
								if (message.includes('o2-api-key') || message.includes('o2source=browser')) {
									return false;
								}
							}

							if (event.usr) {
								const latestUser = latestUserContextRef.current;
								Object.assign(event.usr, {
									id: event.usr.id ?? latestUser.id,
									email: event.usr.email ?? latestUser.userName,
									siteName: event.usr.siteName ?? latestUser.siteName,
									userName: event.usr.userName ?? latestUser.userName,
								});
							}

							// Fix abnormal view.url and view.name (route transition states with '?')
							if (event.type === 'view' || event.type === 'action') {
								if (event.view) {
									const viewName = event.view.name || '';

									// Detect and fix paths containing '?'
									// Example: /en-US/sports/?/l → /en-US/sports
									if (viewName.includes('?')) {
										// Remove '?' and everything after it
										const cleanedPath = viewName.split('?')[0];
										event.view.name = cleanedPath;

										// Also fix URL
										if (event.view.url) {
											try {
												// Layer 3: Protect URL parsing (can throw on malformed URLs)
												const url = new URL(event.view.url);
												url.pathname = cleanedPath;
												url.search = '';
												event.view.url = url.toString();
											} catch (urlError) {
												// URL parsing failed, silently skip URL modification
												console.warn('OpenObserve: Failed to parse URL', urlError);
											}
										}
									}
								}
							}
						} catch (error) {
							// Don't block event sending if beforeSend processing fails
							console.error('OpenObserve beforeSend error:', error);
						}
						return true; // Always return true to continue sending event
					}
				});

				// Forward runtime errors and console output to O2 logs (same shape as the poc-rum demo)
				openobserveLogs.init({
					clientToken,
					site,
					organizationIdentifier: OPENOBSERVE_ORGANIZATION_IDENTIFIER,
					apiVersion: 'v1',
					insecureHTTP: false,
					service,
					env: environment.toLowerCase(),
					version: process.env.NEXT_PUBLIC_FILE_VERSION_ID,
					forwardErrorsToLogs: true,
					forwardConsoleLogs: ['log', 'warn', 'error'],
					sessionSampleRate: licSettingsData.sportsbook?.openObserveSampleRateList?.[2] ?? 0,
					// Same intake-noise problem as the RUM beforeSend above: forwardErrorsToLogs
					// captures the SDK's own failed uploads to O2 ("Fetch error POST .../rum?o2source=...")
					// because this fork's intake exclusion checks Datadog's dd* params instead of o2*.
					// Drop those self-referential log entries.
					beforeSend: (event) => {
						const message = event.message ?? '';
						if (message.includes('o2-api-key') || message.includes('o2source=browser')) {
							return false;
						}
						return true;
					},
				});

				// Mark as initialized to prevent re-initialization
				isInitializedRef.current = true;

				// Set initial context (Effect 2 will handle view updates)
				try {
					// Signal other call sites (e.g. deeplink tracking) that it's now safe
					// to send events with complete usr context.
					markOpenObserveReady();

					// 設定自訂全域 context
					const viewName = view === 7 ? 'galaxy' : 'mars';
					openobserveRum.setGlobalContextProperty('view', viewName);
					openobserveRum.addFeatureFlagEvaluation('view_type', viewName);

					// 手動啟動初始視圖，避免自動產生帶 ???? 的視圖名稱
					const currentPath = window.location.pathname;
					const cleanPath = currentPath.includes('?')
						? currentPath.split('?')[0]
						: currentPath;

					openobserveRum.startView({
						name: cleanPath,
						service,
						version: process.env.NEXT_PUBLIC_FILE_VERSION_ID
					});

					// 啟動 Session Replay 錄製（依 sessionReplaySampleRate 取樣）
					openobserveRum.startSessionReplayRecording();

					// 延遲設定 CDN 資訊，確保 CDNInfoProvider 已讀取 meta tag
					setTimeout(() => {
						try {
							const cdnInfo = getCDNInfo();
							// 只設定 CDN 供應商（cloudflare, MCDN, cdnetworks, thunder）
							if (cdnInfo?.provider) {
								openobserveRum.setGlobalContextProperty('cdn.provider', cdnInfo.provider);
							}
						} catch (cdnError) {
							console.warn('OpenObserve: Failed to set CDN info', cdnError);
						}
					}, 100);
				} catch (contextError) {
					console.error('OpenObserve: Failed to set initial context', contextError);
				}
			} catch (error) {
				// If OpenObserve initialization fails completely, log but don't crash the app
				console.error('OpenObserve initialization failed:', error);
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isBefore, licSettingsData?.sportsbook?.enableOpenObserve, licSettingsData?.sportsbook?.openObserveClientToken, licSettingsData?.sportsbook?.openObserveSite, user?.ID, authData?.siteName, authData?.userName]);
	// Note: Only initialize OpenObserve for logged-in users (isBefore = false)

	// Effect 2: Update view context when view changes (after initialization)
	useEffect(() => {
		if (isInitializedRef.current) {
			try {
				const viewName = view === 7 ? 'galaxy' : 'mars';
				openobserveRum.setGlobalContextProperty('view', viewName);
				openobserveRum.addFeatureFlagEvaluation('view_type', viewName);
			} catch (error) {
				console.error('OpenObserve: Failed to update view context', error);
			}
		}
	}, [view]);

	// Effect 3: Update user session when user data loads (after initialization)
	useEffect(() => {
		if (isInitializedRef.current && user?.ID) {
			try {
				openobserveRum.setUser({
					id: String(user.ID),
					// Standard User field the O2 Sessions/Replay UI keys the user display on
					// (without it the list shows "Unknown"; it doesn't read our custom userName)
					email: authData?.userName || undefined,
					siteName: authData?.siteName || undefined,
					userName: authData?.userName || undefined,
				});
			} catch (error) {
				console.error('OpenObserve: Failed to set user session', error);
			}
		}
	}, [user?.ID, authData?.siteName, authData?.userName]);

	// Effect 4: Set CDN information to OpenObserve (after initialization and when CDN info is available)
	useEffect(() => {
		if (!isInitializedRef.current) {
			return;
		}

		// 延遲讀取，確保 CDNInfoProvider 已讀取 meta tag
		const timer = setTimeout(() => {
			try {
				const cdnInfo = getCDNInfo();
				// 只設定 CDN 供應商（cloudflare, MCDN, cdnetworks, thunder）
				if (cdnInfo?.provider) {
					openobserveRum.setGlobalContextProperty('cdn.provider', cdnInfo.provider);
				}
			} catch (error) {
				console.error('OpenObserve: Failed to set CDN info', error);
			}
		}, 100); // 100ms delay to ensure meta tag is read

		return () => clearTimeout(timer);
	}, []); // 只在元件掛載後執行一次

	return null;
};
